/**
 * Stabilizers (37.17): the move-versus-shot trade, as a match setting.
 *
 * A launcher needs a settled footing. With stabilizers on, every movement
 * point a unit spends this turn leaves its mount less settled: the maximum
 * shot power drops and the aim preview shortens. A unit that holds position
 * fires at full power with its full preview. Off (the default) is the game as
 * it has always played.
 *
 * Pure, deterministic, and read by humans, bots and the headless harness alike:
 * `createProjectile` clamps the power, the preview scales its quality, and the
 * bot fire search caps its sweep.
 */

import type { PlayerState, GameConfig } from '../types';

/** Fully unsettled (every point spent) still fires at this fraction of full power / preview. */
export const STABILIZER_MIN_FRACTION = 0.4;

export function stabilizersOn(config: Pick<GameConfig, 'stabilizers'> | undefined): boolean {
  return config?.stabilizers === true;
}

/** How settled the mount is this turn: 1 with all movement left, 0 with none. */
export function settleFraction(player: Pick<PlayerState, 'movementRemaining' | 'movementRange'>): number {
  const range = player.movementRange > 0 ? player.movementRange : 1;
  return Math.max(0, Math.min(1, player.movementRemaining / range));
}

/** Multiplier on power ceiling and preview quality: 1 when settled, STABILIZER_MIN_FRACTION when fully unsettled. */
export function stabilityFactor(player: Pick<PlayerState, 'movementRemaining' | 'movementRange'>, config: Pick<GameConfig, 'stabilizers'> | undefined): number {
  if (!stabilizersOn(config)) return 1;
  return STABILIZER_MIN_FRACTION + (1 - STABILIZER_MIN_FRACTION) * settleFraction(player);
}

/** The highest power this unit may fire at right now (100 with stabilizers off). */
export function maxFirePower(player: Pick<PlayerState, 'movementRemaining' | 'movementRange'>, config: Pick<GameConfig, 'stabilizers'> | undefined): number {
  return Math.round(100 * stabilityFactor(player, config));
}

/** Clamp a requested power to what the unit can deliver. */
export function clampFirePower(power: number, player: Pick<PlayerState, 'movementRemaining' | 'movementRange'>, config: Pick<GameConfig, 'stabilizers'> | undefined): number {
  return Math.max(5, Math.min(maxFirePower(player, config), power));
}
